import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { FormField } from '@/shared/components/ui/FormField';
import { Switch } from '@/shared/components/ui/Switch';
import { Icon } from '@/shared/icons';
import { useCredits } from '../hooks';

/**
 * Settings card for auto-recharge configuration.
 * CSS variables for page styling (not modal).
 *
 * Only available for credit_card subscriptions.
 * Switch toggle immediately enables/disables (no save button for toggle).
 * Config fields (threshold, amount, max) require save button.
 */
export const AutoRechargeConfig: React.FC = () => {
  const { dashboard, updateAutoRecharge, isUpdatingAutoRecharge } = useCredits();

  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState('');
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [maxPerCycle, setMaxPerCycle] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  const isPixPayment = dashboard?.subscription?.paymentMethod === 'pix';

  // Sync state from dashboard
  useEffect(() => {
    if (dashboard?.autoRecharge) {
      setEnabled(dashboard.autoRecharge.enabled);
      setThreshold(String(dashboard.autoRecharge.thresholdBalance || ''));
      setRechargeAmount(String(dashboard.autoRecharge.rechargeAmount || ''));
      setMaxPerCycle(dashboard.autoRecharge.maxRechargesPerCycle ? String(dashboard.autoRecharge.maxRechargesPerCycle) : '');
      setHasChanges(false);
    }
  }, [dashboard?.autoRecharge]);

  const handleToggle = (val: boolean) => {
    setEnabled(val);

    if (!val) {
      // Disable immediately — no need for config fields
      updateAutoRecharge({ enabled: false });
    }
    // When enabling, user still needs to fill config and save
    // (unless config is already filled from previous state)
    else if (threshold && rechargeAmount) {
      const thresholdNum = parseInt(threshold, 10);
      const rechargeNum = parseInt(rechargeAmount, 10);
      const maxNum = maxPerCycle ? parseInt(maxPerCycle, 10) : undefined;

      if (!isNaN(thresholdNum) && !isNaN(rechargeNum) && thresholdNum > 0 && rechargeNum > 0) {
        updateAutoRecharge({
          enabled: true,
          thresholdBalance: thresholdNum,
          rechargeAmount: rechargeNum,
          maxRechargesPerCycle: maxNum && !isNaN(maxNum) ? maxNum : undefined,
        });
      }
    }
  };

  const handleSave = () => {
    const thresholdNum = parseInt(threshold, 10);
    const rechargeNum = parseInt(rechargeAmount, 10);
    const maxNum = maxPerCycle ? parseInt(maxPerCycle, 10) : undefined;

    updateAutoRecharge({
      enabled,
      ...(enabled ? {
        thresholdBalance: isNaN(thresholdNum) ? undefined : thresholdNum,
        rechargeAmount: isNaN(rechargeNum) ? undefined : rechargeNum,
        maxRechargesPerCycle: maxNum && !isNaN(maxNum) ? maxNum : undefined,
      } : {}),
    }, {
      onSuccess: () => setHasChanges(false),
    });
  };

  // If payment is PIX, don't show this component at all
  if (isPixPayment) {
    return (
      <Card className="rounded-xl">
        <CardHeader className="pb-2 py-3 px-4">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#ede9fe' }}
            >
              <Icon name="refresh" className="h-4 w-4" style={{ color: '#8b5cf6' }} />
            </div>
            <div>
              <h4
                className="text-sm font-semibold"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Recarga Automática
              </h4>
              <p
                className="text-xs"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Disponivel apenas para pagamento via cartão de crédito
              </p>
            </div>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl">
      <CardHeader className="pb-2 py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#ede9fe' }}
            >
              <Icon name="refresh" className="h-4 w-4" style={{ color: '#8b5cf6' }} />
            </div>
            <div>
              <h4
                className="text-sm font-semibold"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Recarga Automática
              </h4>
              <p
                className="text-xs"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Recarregue créditos automaticamente
              </p>
            </div>
          </div>
          <Switch
            checked={enabled}
            onChange={handleToggle}
            size="sm"
            disabled={isUpdatingAutoRecharge}
          />
        </div>
      </CardHeader>

      {enabled && (
        <CardContent className="px-4 pb-4">
          <div className="space-y-4 pt-2">
            <FormField
              label="Saldo minimo"
              description="Recarregar quando o saldo atingir este valor"
            >
              <Input
                type="number"
                value={threshold}
                onChange={(e) => {
                  setThreshold(e.target.value);
                  setHasChanges(true);
                }}
                placeholder="Ex: 50"
                min="1"
              />
            </FormField>

            <FormField
              label="Quantidade de recarga"
              description="Créditos a adicionar por recarga"
            >
              <Input
                type="number"
                value={rechargeAmount}
                onChange={(e) => {
                  setRechargeAmount(e.target.value);
                  setHasChanges(true);
                }}
                placeholder="Ex: 500"
                min="1"
              />
            </FormField>

            <FormField
              label="Maximo por ciclo"
              description="Limite de recargas por ciclo (opcional)"
            >
              <Input
                type="number"
                value={maxPerCycle}
                onChange={(e) => {
                  setMaxPerCycle(e.target.value);
                  setHasChanges(true);
                }}
                placeholder="Sem limite"
                min="1"
              />
            </FormField>

            {hasChanges && (
              <Button
                onClick={handleSave}
                loading={isUpdatingAutoRecharge}
                className="w-full"
              >
                Salvar Configuração
              </Button>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

AutoRechargeConfig.displayName = 'AutoRechargeConfig';
